import { useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.bearing-intelligence.com';

const STEP_ICONS = {
  briefing: (
    <svg viewBox="0 0 72 72" width="72" height="72" style={{display:'block',margin:'0 auto'}}>
      <rect fill="#141b27" stroke="#b8956a" strokeWidth="1" x="0" y="0" width="72" height="72" rx="8"/>
      <rect fill="none" stroke="#c4a563" strokeWidth="1" x="18" y="10" width="36" height="44" rx="3"/>
      <line stroke="#c4a563" strokeWidth="1.2" strokeLinecap="round" x1="24" y1="22" x2="48" y2="22"/>
      <line stroke="#c4a563" strokeWidth="1.2" strokeLinecap="round" x1="24" y1="30" x2="48" y2="30"/>
      <line stroke="#c4a563" strokeWidth="1.2" strokeLinecap="round" x1="24" y1="38" x2="40" y2="38"/>
      <polygon fill="#c4a563" points="36,26 39,40 36,44 33,40"/>
      <polygon fill="#1e2a3a" stroke="#c4a563" strokeWidth="0.5" points="36,44 39,54 36,50 33,54"/>
    </svg>
  ),
  pe: (
    <svg viewBox="0 0 72 72" width="72" height="72" style={{display:'block',margin:'0 auto'}}>
      <rect fill="#141b27" stroke="#b8956a" strokeWidth="1" x="0" y="0" width="72" height="72" rx="8"/>
      <circle fill="none" stroke="#b8956a" strokeWidth="0.8" opacity="0.3" cx="36" cy="36" r="28"/>
      <circle fill="none" stroke="#b8956a" strokeWidth="0.8" opacity="0.5" cx="36" cy="36" r="20"/>
      <circle fill="none" stroke="#c4a563" strokeWidth="1" cx="36" cy="36" r="12"/>
      <circle fill="#c4a563" cx="36" cy="36" r="3"/>
      <line stroke="#c4a563" strokeWidth="1.5" strokeLinecap="round" x1="36" y1="36" x2="36" y2="14"/>
      <line stroke="#b8956a" strokeWidth="1" strokeLinecap="round" opacity="0.6" x1="36" y1="36" x2="52" y2="48"/>
    </svg>
  ),
  globe: (
    <svg viewBox="0 0 72 72" width="72" height="72" style={{display:'block',margin:'0 auto'}}>
      <rect fill="#141b27" stroke="#b8956a" strokeWidth="1" x="0" y="0" width="72" height="72" rx="8"/>
      <circle fill="none" stroke="#c4a563" strokeWidth="1" cx="36" cy="36" r="22"/>
      <ellipse fill="none" stroke="#b8956a" strokeWidth="0.8" opacity="0.5" cx="36" cy="36" rx="12" ry="22"/>
      <line stroke="#b8956a" strokeWidth="0.8" opacity="0.4" x1="14" y1="36" x2="58" y2="36"/>
      <line stroke="#b8956a" strokeWidth="0.8" opacity="0.3" x1="16" y1="25" x2="56" y2="25"/>
      <line stroke="#b8956a" strokeWidth="0.8" opacity="0.3" x1="16" y1="47" x2="56" y2="47"/>
      <polygon fill="#c4a563" points="44,22 46,28 44,30 42,28"/>
    </svg>
  ),
  country: (
    <svg viewBox="0 0 72 72" width="72" height="72" style={{display:'block',margin:'0 auto'}}>
      <rect fill="#141b27" stroke="#b8956a" strokeWidth="1" x="0" y="0" width="72" height="72" rx="8"/>
      <line stroke="#c4a563" strokeWidth="1.2" strokeLinecap="round" x1="32" y1="12" x2="32" y2="60"/>
      <rect fill="#c4a563" opacity="0.9" x="32" y="12" width="20" height="13" rx="1"/>
      <rect fill="#1e2a3a" opacity="0.5" x="32" y="19" width="20" height="6"/>
      <path fill="none" stroke="#b8956a" strokeWidth="1" opacity="0.6" d="M 18,28 L 10,36 L 18,44"/>
      <path fill="none" stroke="#b8956a" strokeWidth="1" opacity="0.6" d="M 46,28 L 54,36 L 46,44"/>
    </svg>
  ),
  dashboard: (
    <svg viewBox="0 0 72 72" width="72" height="72" style={{display:'block',margin:'0 auto'}}>
      <rect fill="#141b27" stroke="#b8956a" strokeWidth="1" x="0" y="0" width="72" height="72" rx="8"/>
      <circle fill="none" stroke="#b8956a" strokeWidth="0.6" opacity="0.3" cx="36" cy="36" r="26"/>
      <line stroke="#b8956a" strokeWidth="0.6" opacity="0.25" x1="36" y1="10" x2="36" y2="62"/>
      <line stroke="#b8956a" strokeWidth="0.6" opacity="0.25" x1="10" y1="36" x2="62" y2="36"/>
      <line stroke="#b8956a" strokeWidth="0.6" opacity="0.2" x1="17" y1="17" x2="55" y2="55"/>
      <line stroke="#b8956a" strokeWidth="0.6" opacity="0.2" x1="55" y1="17" x2="17" y2="55"/>
      <polygon fill="#c4a563" points="36,14 39,33 36,36 33,33"/>
      <polygon fill="#b8956a" opacity="0.5" points="36,58 39,39 36,36 33,39"/>
      <polygon fill="#c4a563" opacity="0.7" points="58,36 39,39 36,36 39,33"/>
      <polygon fill="#c4a563" opacity="0.7" points="14,36 33,39 36,36 33,33"/>
      <circle fill="#c4a563" cx="36" cy="36" r="3"/>
    </svg>
  ),
};

const STEPS = [
  {
    title: "Today's Briefing",
    icon: 'briefing',
    desc: "Each day at 0500 ET, a new intelligence briefing is published. Open with the audio player for a narrated summary, or read through the Global Events — each with confidence intervals and probabilistic assessments."
  },
  {
    title: "Probabilistic Estimates",
    icon: 'pe',
    desc: "Each Key Development includes a hypothesis and platform probability estimate (PE). Use the slider to submit your own estimate — Likely, Monitor, or Unlikely. Your accuracy over time builds your Analyst Score."
  },
  {
    title: "Global Navigation",
    icon: 'globe',
    desc: "The interactive map shows escalation levels across 196 countries. Use the Reference Location selector to see the world from any country's perspective — a red-team analytical tool."
  },
  {
    title: "Country Intelligence",
    icon: 'country',
    desc: "Click any country on the map to open its panel: current status, escalation level, factbook data across demographics, economy, military, and government."
  },
  {
    title: "My Dashboard",
    icon: 'dashboard',
    desc: "Track your open and resolved hypotheses, your Analyst Score, and how your estimates compared to the platform. Your score updates automatically when hypotheses resolve."
  }
];

export default function InvitationPage({ token, onSuccess }) {
  const [step, setStep] = useState('welcome'); // welcome | eula | orientation | register
  const [eulaAccepted, setEulaAccepted] = useState(false);
  const [orientStep, setOrientStep] = useState(0);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [tokenValid, setTokenValid] = useState(null);

  useEffect(() => {
    // Validate token and pre-fill email
    fetch(`${API_URL}/api/beta/validate-invite/${token}`)
      .then(r => r.json())
      .then(d => {
        if (d.valid) {
          setTokenValid(true);
          if (d.email) setEmail(d.email);
          if (d.name) setName(d.name);
        } else {
          setTokenValid(false);
        }
      })
      .catch(() => setTokenValid(false));
  }, [token]);

  const handleRegister = async () => {
    if (!password || password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/auth/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, invite_token: token, name })
      });
      const data = await res.json();
      if (data.token) {
        // Clear invite URL so refresh doesn't show invalid invitation
        window.history.replaceState({}, '', '/');
        onSuccess(data.token, data.user);
      } else if (data.error && data.error.includes('already registered')) {
        setError('This email already has an account. Please use the login page instead.');
      } else {
        setError(data.error || 'Registration failed');
      }
    } catch(e) {
      setError('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (tokenValid === null) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.logo}>⚓</div>
          <p style={styles.muted}>Validating invitation…</p>
        </div>
      </div>
    );
  }

  if (tokenValid === false) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.logo}>⚓</div>
          <h2 style={styles.title}>Invalid Invitation</h2>
          <p style={styles.muted}>This invitation link is invalid or has already been used. Contact your administrator for a new invitation.</p>
        </div>
      </div>
    );
  }

  // WELCOME
  if (step === 'welcome') return (
    <div style={styles.container}>
      <div style={{...styles.card, maxWidth: 560}}>
        <div style={styles.compassRose}>⚓</div>
        <div style={styles.eyebrow}>BEARING INTELLIGENCE</div>
        <h1 style={styles.headline}>Know your bearing.</h1>
        <div style={styles.divider} />
        <p style={styles.body}>
          Strategic global intelligence — clearly described, precisely assessed, 
          and delivered in under ten minutes a day.
        </p>
        <p style={styles.body}>
          Bearing Intelligence serves leaders in industry, academia, and government 
          who need to stay informed without the noise. We track the developments 
          that matter, assess their probability, and give you the analytical 
          framework to navigate an uncertain world.
        </p>
        <p style={styles.body}>
          Every briefing is built from open-source reporting, assessed with 
          probabilistic rigor, and narrated for your commute. We work to keep 
          you informed and keep you on course.
        </p>
        <div style={styles.tagline}>Stay informed. Stay on course.</div>
        <div style={styles.betaBadge}>BETA ACCESS · {name ? `Welcome, ${name}` : 'Welcome'}</div>
        <button style={styles.btn} onClick={() => setStep('eula')}>
          Begin Onboarding →
        </button>
      </div>
    </div>
  );

  // EULA
  if (step === 'eula') return (
    <div style={styles.container}>
      <div style={{...styles.card, maxWidth: 600}}>
        <div style={styles.eyebrow}>TERMS OF ACCESS</div>
        <h2 style={styles.title}>Beta Agreement & Privacy</h2>
        <div style={styles.eulaScroll}>
          <h3 style={styles.eulaSection}>Beta Program</h3>
          <p style={styles.eulaText}>
            You have been invited to participate in the Bearing Intelligence closed beta program. 
            As a beta participant, you agree to provide honest feedback to help improve the platform. 
            Your insights are genuinely valued — this platform is shaped by its readers.
          </p>
          <p style={styles.eulaText}>
            Beta access is provided at no cost. Features may change, and some functionality 
            may be incomplete or experimental. We appreciate your patience and candor.
          </p>

          <h3 style={styles.eulaSection}>Data & Privacy</h3>
          <p style={styles.eulaText}>
            Bearing Intelligence collects only what is necessary to operate the platform:
          </p>
          <ul style={styles.eulaList}>
            <li>Your email address, used for authentication only</li>
            <li>Anonymous device identifiers for Analyst Score tracking</li>
            <li>Probabilistic estimates you submit, stored anonymously</li>
            <li>Voluntary feedback you submit through the feedback system</li>
          </ul>
          <p style={styles.eulaText}>
            We will never sell, share, or transfer your personal data to third parties. 
            No advertising networks. No data brokers. No exceptions.
          </p>
          <p style={styles.eulaText}>
            Anonymous behavioral signals — including your PE estimates and hypothesis 
            resolution accuracy — may be used to improve the platform's Bayesian prior 
            calibration, making assessments more accurate over time. This data is 
            never linked to your identity.
          </p>

          <h3 style={styles.eulaSection}>Content</h3>
          <p style={styles.eulaText}>
            All content is derived from open-source, publicly available information. 
            Bearing Intelligence is not affiliated with any government agency. 
            No content represents classified information or official government assessment. 
            Analysis is provided for informational and educational purposes only and 
            does not constitute investment, legal, or policy advice.
          </p>

          <h3 style={styles.eulaSection}>Feedback & Improvement</h3>
          <p style={styles.eulaText}>
            Your feedback during beta is the most valuable input we have. When you 
            submit feedback, it is reviewed by our team and may generate a proposed 
            platform update. High-priority feedback directly shapes our development roadmap.
          </p>
          <p style={styles.eulaText}>
            We ask that you treat this as a genuine collaboration. Tell us what works, 
            what doesn't, and what you wish existed. The platform you're helping build 
            will be used by decision-makers who need it to be excellent.
          </p>
        </div>

        <label style={styles.checkLabel}>
          <input
            type="checkbox"
            checked={eulaAccepted}
            onChange={e => setEulaAccepted(e.target.checked)}
            style={{ marginRight: 10, accentColor: '#E74C3C' }}
          />
          <span style={{ color: '#e2e8f0', fontSize: 14 }}>
            I have read and agree to these terms
          </span>
        </label>

        <div style={{ display:'flex', gap:12, marginTop:20 }}>
          <button style={styles.btnSecondary} onClick={() => setStep('welcome')}>
            ← Back
          </button>
          <button
            style={{...styles.btn, flex:1, opacity: eulaAccepted ? 1 : 0.4}}
            disabled={!eulaAccepted}
            onClick={() => setStep('orientation')}
          >
            Continue →
          </button>
        </div>
      </div>
    </div>
  );

  // ORIENTATION
  if (step === 'orientation') {
    const current = STEPS[orientStep];
    const isLast = orientStep === STEPS.length - 1;
    return (
      <div style={styles.container}>
        <div style={{...styles.card, maxWidth: 520}}>
          <div style={styles.eyebrow}>ORIENTATION · {orientStep + 1} OF {STEPS.length}</div>
          <div style={styles.progressBar}>
            <div style={{
              ...styles.progressFill,
              width: `${((orientStep + 1) / STEPS.length) * 100}%`
            }} />
          </div>
          <div style={{ margin: '20px 0 12px', textAlign:'center' }}>
            {STEP_ICONS[current.icon]}
          </div>
          <h2 style={styles.title}>{current.title}</h2>
          <p style={{...styles.body, textAlign:'center', marginBottom: 32}}>
            {current.desc}
          </p>
          <div style={{ display:'flex', gap:12 }}>
            {orientStep > 0 && (
              <button style={styles.btnSecondary} onClick={() => setOrientStep(s => s-1)}>
                ← Back
              </button>
            )}
            <button
              style={{...styles.btn, flex:1}}
              onClick={() => isLast ? setStep('register') : setOrientStep(s => s+1)}
            >
              {isLast ? 'Create Account →' : 'Next →'}
            </button>
          </div>
          <button
            style={styles.skip}
            onClick={() => setStep('register')}
          >
            Skip orientation
          </button>
        </div>
      </div>
    );
  }

  // REGISTER
  return (
    <div style={styles.container}>
      <div style={{...styles.card, maxWidth: 440}}>
        <div style={styles.eyebrow}>CREATE YOUR ACCOUNT</div>
        <h2 style={styles.title}>Set your password</h2>
        <p style={styles.muted}>
          Your account will be created with beta premium access.
        </p>
        <div style={{ marginTop: 24 }}>
          <div style={styles.field}>
            <label style={styles.label}>Name</label>
            <input
              style={styles.input}
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="Your name"
            />
          </div>
          <div style={styles.field}>
            <label style={styles.label}>Email</label>
            <input
              style={{...styles.input, opacity: 0.7}}
              value={email}
              readOnly
            />
          </div>
          <div style={styles.field}>
            <label style={styles.label}>Password</label>
            <input
              style={styles.input}
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="Minimum 8 characters"
              onKeyDown={e => e.key === 'Enter' && handleRegister()}
            />
          </div>
          {error && <div style={styles.error}>{error}</div>}
          <button
            style={{...styles.btn, width:'100%', marginTop:8}}
            onClick={handleRegister}
            disabled={loading}
          >
            {loading ? 'Creating account…' : 'Enter Bearing Intelligence →'}
          </button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#090d14',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '24px 16px',
    fontFamily: "'Inter', -apple-system, sans-serif",
  },
  card: {
    width: '100%',
    backgroundColor: '#141b27',
    border: '1px solid #1e2a3a',
    borderRadius: 10,
    padding: '40px 36px',
    boxShadow: '0 4px 24px rgba(0,0,0,0.6)',
  },
  compassRose: {
    fontSize: 48,
    textAlign: 'center',
    marginBottom: 16,
  },
  eyebrow: {
    fontSize: 10,
    fontWeight: 600,
    letterSpacing: '1.5px',
    textTransform: 'uppercase',
    color: '#4a5568',
    textAlign: 'center',
    marginBottom: 12,
  },
  headline: {
    fontFamily: "'Playfair Display', Georgia, serif",
    fontSize: 42,
    fontWeight: 700,
    color: '#E74C3C',
    textAlign: 'center',
    lineHeight: 1.15,
    marginBottom: 8,
  },
  title: {
    fontFamily: "'Playfair Display', Georgia, serif",
    fontSize: 26,
    fontWeight: 600,
    color: '#E74C3C',
    textAlign: 'center',
    marginBottom: 16,
    lineHeight: 1.3,
  },
  divider: {
    height: 1,
    backgroundColor: '#1e2a3a',
    margin: '20px 0',
  },
  body: {
    fontSize: 15,
    color: '#8896a8',
    lineHeight: 1.75,
    marginBottom: 14,
  },
  tagline: {
    fontFamily: "'Playfair Display', Georgia, serif",
    fontSize: 16,
    fontStyle: 'italic',
    color: '#b8956a',
    textAlign: 'center',
    margin: '20px 0 8px',
    letterSpacing: '0.3px',
  },
  betaBadge: {
    fontSize: 10,
    fontWeight: 600,
    letterSpacing: '1px',
    textTransform: 'uppercase',
    color: '#3d6494',
    textAlign: 'center',
    marginBottom: 24,
  },
  btn: {
    backgroundColor: '#E74C3C',
    color: '#090d14',
    border: 'none',
    borderRadius: 4,
    padding: '12px 24px',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    width: '100%',
    fontFamily: "'Inter', sans-serif",
    letterSpacing: '0.3px',
    transition: 'opacity 0.15s',
  },
  btnSecondary: {
    backgroundColor: 'transparent',
    color: '#8896a8',
    border: '1px solid #1e2a3a',
    borderRadius: 4,
    padding: '12px 20px',
    fontSize: 14,
    cursor: 'pointer',
    fontFamily: "'Inter', sans-serif",
    whiteSpace: 'nowrap',
  },
  skip: {
    background: 'none',
    border: 'none',
    color: '#4a5568',
    fontSize: 12,
    cursor: 'pointer',
    textAlign: 'center',
    width: '100%',
    marginTop: 16,
    padding: '4px 0',
  },
  progressBar: {
    height: 3,
    backgroundColor: '#1e2a3a',
    borderRadius: 2,
    marginBottom: 28,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    backgroundColor: '#E74C3C',
    borderRadius: 2,
    transition: 'width 0.3s ease',
  },
  eulaScroll: {
    maxHeight: 320,
    overflowY: 'auto',
    backgroundColor: '#0f1520',
    border: '1px solid #1e2a3a',
    borderRadius: 6,
    padding: '16px 18px',
    marginBottom: 20,
  },
  eulaSection: {
    fontFamily: "'Inter', sans-serif",
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: '0.8px',
    textTransform: 'uppercase',
    color: '#E74C3C',
    marginTop: 16,
    marginBottom: 8,
  },
  eulaText: {
    fontSize: 13,
    color: '#8896a8',
    lineHeight: 1.7,
    marginBottom: 10,
  },
  eulaList: {
    fontSize: 13,
    color: '#8896a8',
    lineHeight: 1.9,
    paddingLeft: 20,
    marginBottom: 10,
  },
  checkLabel: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    padding: '8px 0',
  },
  field: {
    marginBottom: 16,
  },
  label: {
    display: 'block',
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: '0.8px',
    textTransform: 'uppercase',
    color: '#4a5568',
    marginBottom: 6,
  },
  input: {
    width: '100%',
    backgroundColor: '#0f1520',
    border: '1px solid #1e2a3a',
    borderRadius: 4,
    padding: '10px 14px',
    fontSize: 14,
    color: '#e2e8f0',
    fontFamily: "'Inter', sans-serif",
    outline: 'none',
    minHeight: 44,
  },
  muted: {
    fontSize: 14,
    color: '#4a5568',
    textAlign: 'center',
    lineHeight: 1.6,
    marginTop: 8,
  },
  error: {
    color: '#c94040',
    fontSize: 13,
    padding: '8px 12px',
    backgroundColor: 'rgba(201,64,64,0.1)',
    border: '1px solid rgba(201,64,64,0.2)',
    borderRadius: 4,
    marginBottom: 12,
  },
};
